import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/* ============================================================================
   Dashboard — Server Health (READ-ONLY, 2026-10-25)
   Five tiles (Server Health, Disk Usage, System Health, Application Health,
   Server Patches) for the central server or any branch server.
   Shown only to the health-check viewers (server-enforced allowlist).
   ============================================================================ */

type Item = { label: string; status: 'ok' | 'warn' | 'crit' | 'info'; value: string; detail?: string; issue?: string; action?: string };
type Tile = { id: string; label: string; status: Item['status']; counts: Record<string, number>; items: Item[] };
type Summary = { server: string; name: string; generated_at: string; online: boolean; last_report?: string; overall: Item['status']; tiles: Tile[] };
type Server = { id: string; name: string; kind: string; online?: boolean };

const ST: Record<string, { label: string; icon: string }> = {
  ok: { label: 'Healthy', icon: '✓' }, warn: { label: 'Attention', icon: '!' },
  crit: { label: 'Critical', icon: '✕' }, info: { label: 'Info', icon: 'i' },
};
const ICONS: Record<string, string> = { server: '🖥', disk: '💽', system: '⚙', apps: '▦', patches: '⬆' };

const CSS = `
.sh{margin-bottom:18px}
.sh-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:10px}
.sh-head h2{margin:0;font-size:17px;font-weight:700;color:#1f2733}
.sh-sel{display:flex;gap:4px;background:#eef0f4;border-radius:9px;padding:3px;flex-wrap:wrap}
.sh-sel button{border:0;background:none;padding:6px 12px;border-radius:7px;font-size:13px;font-weight:600;color:#4a5363;cursor:pointer;display:flex;align-items:center;gap:6px}
.sh-sel button.on{background:#fff;color:#1f2733;box-shadow:0 1px 2px rgba(0,0,0,.08)}
.sh-sel .dot{width:8px;height:8px;border-radius:50%}
.sh-tools{margin-left:auto;display:flex;gap:8px;align-items:center;font-size:12px;color:#5b6474}
.sh-btn{border:1px solid #cfd5de;background:#fff;border-radius:8px;padding:6px 12px;font-size:12px;font-weight:600;cursor:pointer;color:#1f2733}
.sh-banner{border-radius:10px;padding:9px 14px;font-size:13px;margin-bottom:10px;font-weight:600}
.sh-banner.crit{background:#fdecec;border:1px solid #f3b5b5;color:#9b1c1c}
.sh-banner.warn{background:#fff6e5;border:1px solid #f5d79a;color:#7a5600}
.sh-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:12px}
.sh-tile{background:#fff;border:1px solid #e3e6eb;border-radius:12px;padding:12px 14px;border-top:4px solid #7a8496;min-width:0}
.sh-tile.ok{border-top-color:#1f9d55}.sh-tile.warn{border-top-color:#e0a100}.sh-tile.crit{border-top-color:#d93636}
.sh-tile h3{margin:0 0 8px;font-size:14px;font-weight:700;color:#1f2733;display:flex;align-items:center;gap:6px}
.sh-pill{margin-left:auto;font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px}
.sh-pill.ok{background:#e9f8ef;color:#167a42}.sh-pill.warn{background:#fff3d6;color:#8a6200}.sh-pill.crit{background:#fdecec;color:#b42323}.sh-pill.info{background:#eef0f4;color:#4a5363}
.sh-row{display:flex;gap:8px;align-items:flex-start;font-size:12.5px;padding:4px 0;border-bottom:1px solid #f3f4f6}
.sh-row:last-child{border-bottom:0}
.sh-ic{width:16px;height:16px;border-radius:50%;color:#fff;font-size:9px;font-weight:800;display:flex;align-items:center;justify-content:center;flex:none;margin-top:1px}
.sh-ic.ok{background:#1f9d55}.sh-ic.warn{background:#e0a100}.sh-ic.crit{background:#d93636}.sh-ic.info{background:#7a8496}
.sh-row .l{flex:1;min-width:0;color:#374151;overflow-wrap:anywhere}
.sh-row .v{color:#1f2733;font-weight:600;text-align:right;max-width:55%;overflow-wrap:anywhere}
.sh-iss{font-size:11.5px;color:#8a3b00;margin:2px 0 4px 24px}
.sh-more{border:0;background:none;color:var(--z-blue,#2D8CFF);font-size:12px;font-weight:600;cursor:pointer;padding:4px 0 0}
.sh-err{background:#fdecec;border:1px solid #f3b5b5;border-radius:10px;padding:10px 14px;font-size:13px;color:#9b1c1c}
`;

function TileCard({ t }: { t: Tile }) {
  const [open, setOpen] = useState(false);
  const bad = t.items.filter((i) => i.status === 'warn' || i.status === 'crit');
  const shown = open ? t.items : (bad.length ? bad.concat(t.items.filter((i) => !bad.includes(i))).slice(0, Math.max(4, bad.length)) : t.items.slice(0, 4));
  return (
    <div className={`sh-tile ${t.status}`}>
      <h3><span>{ICONS[t.id] || '•'}</span>{t.label}<span className={`sh-pill ${t.status}`}>{ST[t.status]?.label}</span></h3>
      {shown.map((i, k) => (
        <React.Fragment key={k}>
          <div className="sh-row" title={i.detail || ''}>
            <span className={`sh-ic ${i.status}`}>{ST[i.status]?.icon}</span>
            <span className="l">{i.label}</span><span className="v">{i.value}</span>
          </div>
          {(i.status === 'warn' || i.status === 'crit') && i.issue && <div className="sh-iss">{i.issue}{i.action ? ` → ${i.action}` : ''}</div>}
        </React.Fragment>
      ))}
      {t.items.length > shown.length && <button className="sh-more" onClick={() => setOpen(true)}>Show all {t.items.length}</button>}
      {open && t.items.length > 4 && <button className="sh-more" onClick={() => setOpen(false)}>Show less</button>}
    </div>
  );
}

export default function ServerHealthPanel({ onOpen }: { onOpen?: () => void }) {
  const [allowed, setAllowed] = useState(false);
  const [servers, setServers] = useState<Server[]>([]);
  const [sel, setSel] = useState<string>(() => { try { return localStorage.getItem('ce_sh_server') || 'central'; } catch { return 'central'; } });
  const [data, setData] = useState<Summary | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get('/api/health-check/access').then((r) => {
      if (!alive || !r.data?.allowed) return;
      setAllowed(true);
      axios.get('/api/health-check/servers').then((s) => { if (alive) setServers(s.data?.servers || []); }).catch(() => {});
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const load = useCallback(async (force = false) => {
    setBusy(true);
    try {
      const r = await axios.get(`/api/health-check/summary?server=${encodeURIComponent(sel)}${force ? '&refresh=1' : ''}`);
      setData(r.data); setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Server health is temporarily unavailable');
    } finally { setBusy(false); }
  }, [sel]);

  useEffect(() => {
    if (!allowed) return;
    try { localStorage.setItem('ce_sh_server', sel); } catch { /* ignore */ }
    setData(null); load();
    const t = setInterval(() => load(), 60000);
    return () => clearInterval(t);
  }, [allowed, sel, load]);

  if (!allowed) return null;
  const valid = servers.length ? servers : [{ id: 'central', name: 'Dubai — Central server', kind: 'central' }];
  return (
    <div className="sh">
      <style>{CSS}</style>
      <div className="sh-head">
        <h2>Server Health</h2>
        <div className="sh-sel">
          {valid.map((s) => (
            <button key={s.id} className={sel === s.id ? 'on' : ''} onClick={() => setSel(s.id)}>
              {s.kind === 'branch' && <span className="dot" style={{ background: s.online ? '#1f9d55' : '#d93636' }} />}
              {s.name}
            </button>
          ))}
        </div>
        <div className="sh-tools">
          {data && <span>Updated {new Date(data.generated_at).toLocaleTimeString()}</span>}
          <button className="sh-btn" disabled={busy} onClick={() => load(true)}>{busy ? 'Checking…' : 'Refresh'}</button>
          {onOpen && sel === 'central' && <button className="sh-btn" onClick={onOpen}>Full health check</button>}
        </div>
      </div>
      {err && <div className="sh-err">{err}</div>}
      {data && !data.online && <div className="sh-banner crit">{data.name} is not reporting — last report {data.last_report}. Figures below are from its last report.</div>}
      {data && data.online && (data.overall === 'crit' || data.overall === 'warn') && (
        <div className={`sh-banner ${data.overall}`}>{data.overall === 'crit' ? 'Critical issues need attention on ' : 'Some items need attention on '}{data.name}.</div>
      )}
      {data && <div className="sh-grid">{data.tiles.map((t) => <TileCard key={t.id} t={t} />)}</div>}
      {!data && !err && <div style={{ fontSize: 13, color: '#5b6474' }}>Checking server health…</div>}
    </div>
  );
}
